/**
 * Workflow State Management Store (Zustand)
 * Manages workflow, nodes, connections, and groups with API integration
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  Workflow,
  WorkflowNode,
  WorkflowConnection,
  ProcessGroup,
  NodeType,
  NodeCategory,
  ProcessElementData,
  WorkflowNodeRuntime,
} from '@flow-studio/types';
// Local mock data to avoid circular dependency with flow-studio-core
// (flow-studio-core depends on @flow-studio/store)
const mockWorkflowData = {} as Record<string, any>; // Placeholder for now
import { NodeTemplateService } from '../../../../NodeTemplates'; // External sibling package
import { workflowApiClient, processElementApiClient, connectorApiClient, SaveWorkflowResponse, mapNodesToDtosWithTypeResolution, mapEdgesToDtos, mapDtosToNodes, mapDtosToEdges } from '@flow-studio/api';
import { useUIStore } from './uiStore';

// Debounced autosave for mutations (delete/add/etc.) that don't go through the
// canvas's own drag-stop debounce — mirrors WorkflowCanvas's handleNodeDragStop.
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleAutoSave(get: () => WorkflowState): void {
  if (!(useUIStore.getState() as any).autoSaveEnabled) return;
  if (autoSaveTimer) clearTimeout(autoSaveTimer);
  autoSaveTimer = setTimeout(() => {
    get().saveWorkflowToApi();
  }, 800);
}

export interface WorkflowState {
  // Current Workflow
  workflow: Workflow | null;
  nodes: WorkflowNode[];
  edges: WorkflowConnection[];
  groups: ProcessGroup[];

  // Node Type Catalog
  nodeTypes: NodeType[];
  nodeCategories: NodeCategory[];

  // API State - Loading & Errors
  isSaving: boolean;
  isLoading: boolean;
  saveError: string | null;
  loadError: string | null;
  lastSaved: Date | null;

  // Current Context (for API calls)
  currentProcessThreadId: number | null;
  currentVersionId: number | null;

  // Node Type Catalog Actions
  setNodeTypes: (nodeTypes: NodeType[]) => void;
  setNodeCategories: (categories: NodeCategory[]) => void;

  // History for Undo/Redo
  history: {
    past: Array<{ nodes: WorkflowNode[]; edges: WorkflowConnection[] }>;
    future: Array<{ nodes: WorkflowNode[]; edges: WorkflowConnection[] }>;
  };

  // Dirty state tracking
  isDirty: boolean;
  setDirty: (dirty: boolean) => void;
  markDirty: () => void;

  // Actions
  loadWorkflow: (workflowId: string) => void;
  setNodes: (nodes: WorkflowNode[]) => void;
  setEdges: (edges: WorkflowConnection[]) => void;
  addNode: (node: WorkflowNode) => void;
  updateNode: (nodeId: string, updates: Partial<WorkflowNode>) => void;
  updateProcessElement: (nodeId: string, partial: Partial<ProcessElementData>) => void;
  updateRuntime: (nodeId: string, partial: Partial<WorkflowNodeRuntime>) => void;
  deleteNode: (nodeId: string) => void;
  runNode: (nodeId: string) => Promise<void>;
  duplicateNode: (nodeId: string) => Promise<void>;
  addEdge: (edge: WorkflowConnection) => void;
  updateEdge: (edgeId: string, updates: Partial<WorkflowConnection>) => void;
  deleteEdge: (edgeId: string) => void;
  updateAllEdgeTypes: (edgeType: string) => void;
  addGroup: (group: ProcessGroup) => void;
  updateGroup: (groupId: string, updates: Partial<ProcessGroup>) => void;
  deleteGroup: (groupId: string) => void;

  // API Actions
  saveWorkflowToApi: () => Promise<SaveWorkflowResponse | null>;
  loadWorkflowFromApi: (processThreadId: number, versionId: number, workflowName?: string) => Promise<void>;
  refreshWorkflowFromApi: () => Promise<void>;
  setCurrentContext: (processThreadId: number, versionId: number) => void;

  // State Setters
  setSaving: (saving: boolean) => void;
  setLoading: (loading: boolean) => void;
  setSaveError: (error: string | null) => void;
  setLoadError: (error: string | null) => void;

  // Undo/Redo
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  pushHistory: () => void;

  // Utility
  reset: () => void;
}

export const useWorkflowStore = create<WorkflowState>()(
  devtools(
    (set, get) => ({
      // Initial State
      workflow: null,
      nodes: [],
      edges: [],
      groups: [],
      nodeTypes: [],
      nodeCategories: [],
      history: {
        past: [],
        future: [],
      },
      isDirty: false,

      // API State
      isSaving: false,
      isLoading: false,
      saveError: null,
      loadError: null,
      lastSaved: null,
      currentProcessThreadId: null,
      currentVersionId: null,

      // Dirty state management
      setDirty: (dirty: boolean) => set({ isDirty: dirty }),
      markDirty: () => set({ isDirty: true }),

      // Node Type Catalog Management
      setNodeTypes: (nodeTypes: NodeType[]) => {
        set({ nodeTypes });
      },
      setNodeCategories: (categories: NodeCategory[]) => set({ nodeCategories: categories }),

      // API State Setters
      setSaving: (saving: boolean) => set({ isSaving: saving }),
      setLoading: (loading: boolean) => set({ isLoading: loading }),
      setSaveError: (error: string | null) => set({ saveError: error }),
      setLoadError: (error: string | null) => set({ loadError: error }),

      // Set current context
      setCurrentContext: (processThreadId: number, versionId: number) => {
        set({ currentProcessThreadId: processThreadId, currentVersionId: versionId });
      },

      /**
       * Save workflow to backend API
       * Uses the new atomic save endpoint
       */
      saveWorkflowToApi: async (): Promise<SaveWorkflowResponse | null> => {
        const state = get();
        const { currentProcessThreadId, currentVersionId, nodes, edges } = state;

        if (!currentProcessThreadId || !currentVersionId) {
          const errorMsg = 'No workflow context set (processThreadId and versionId required)';
          set({ saveError: errorMsg });
          return null;
        }

        set({ isSaving: true, saveError: null });

        try {
          // Map frontend types to DTOs — resolves ProcessElementTypeID per node via API
          // Pass edges so satellite detection can check if ports are actually connected
          const nodeDtos = await mapNodesToDtosWithTypeResolution(nodes, currentVersionId, edges);
          const edgeDtos = mapEdgesToDtos(edges, currentVersionId);

          // Build save request
          const request = {
            processThreadID: currentProcessThreadId,
            versionID: currentVersionId,
            nodes: nodeDtos,
            edges: edgeDtos,
          };

          // Call API
          const response = await workflowApiClient.saveWorkflow(request);

          if (response.success && response.data) {
            // Mark as clean after successful save
            set({
              isDirty: false,
              lastSaved: new Date(),
              isSaving: false,
              saveError: null,
            });

            // Clear history after save (fresh start)
            set({ history: { past: [], future: [] } });

            // Merge backend-assigned IDs into nodes after save.
            // Newly created nodes get ConnectorID and ProcessElementID assigned by the
            // backend during save. Without this merge the Config button shows
            // "not yet available" and node-specific forms (TIER 1) never load.
            //
            // IMPORTANT: Use the version ID returned in summary.newVersionID rather than
            // currentVersionId. For brand-new workflows, EnsureVersionExistsAsync on the
            // backend may create (or correct) the version, so the actual version ID used
            // may differ from what the frontend had stored. Without this the getByThreadVersion
            // call uses a stale/wrong ID, returns nothing, and connectorID is never merged.
            try {
              const effectiveVersionId = response.data.summary?.newVersionID ?? currentVersionId;
              if (effectiveVersionId && effectiveVersionId !== currentVersionId) {
                set({ currentVersionId: effectiveVersionId });
              }
              const elementsResponse = await processElementApiClient.getByThreadVersion(effectiveVersionId ?? currentVersionId);
              console.log('[workflowStore] post-save getByThreadVersion:', {
                success: elementsResponse.success,
                count: elementsResponse.data?.length ?? 0,
                elements: elementsResponse.data?.map(el => ({
                  key: el.ProcessElementKey,
                  connectorID: el.ConnectorID,
                  processElementID: el.ProcessElementID,
                })),
              });
              if (elementsResponse.success && elementsResponse.data?.length) {
                const connectorIdByKey = new Map<string, number>();
                const processElementIdByKey = new Map<string, number>();
                for (const el of elementsResponse.data) {
                  const key = el.ProcessElementKey;
                  if (!key) continue;
                  if (el.ConnectorID) connectorIdByKey.set(key, el.ConnectorID);
                  if (el.ProcessElementID) processElementIdByKey.set(key, el.ProcessElementID);
                }
                const updatedNodes = get().nodes.map(node => {
                  const connectorID = connectorIdByKey.get(node.id);
                  const processElementID = processElementIdByKey.get(node.id);
                  const needsConnector = connectorID && !node.data.processElement?.connectorID;
                  const needsElementId = processElementID && !node.data.processElement?.processElementID;
                  if (connectorID !== undefined || processElementID !== undefined) {
                    console.log(`[workflowStore] node "${node.id}": backendConnectorID=${connectorID}, currentConnectorID=${node.data.processElement?.connectorID}, needsConnector=${needsConnector}`);
                  }
                  if (needsConnector || needsElementId) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        processElement: {
                          ...node.data.processElement,
                          ...(needsConnector ? { connectorID } : {}),
                          ...(needsElementId ? { processElementID } : {}),
                        },
                      },
                    } as typeof node;
                  }
                  return node;
                });
                set({ nodes: updatedNodes });
              } else {
                console.warn('[workflowStore] post-save: getByThreadVersion returned no elements — connectorID merge skipped', {
                  success: elementsResponse.success,
                  versionId: effectiveVersionId ?? currentVersionId,
                });
              }
            } catch (err) {
              console.warn('[workflowStore] Could not merge backend IDs after save:', err);
            }

            // Save connector configurations to backend.
            // Merges template default connector config (resource, operation, profileName, etc.)
            // with any user-set values from the Config dialog. Template defaults are the base;
            // values already in pe.connector.configuration (set via Config dialog) take priority.
            try {
              const templates = nodeTemplateService.isInitialized() ? nodeTemplateService.getTemplates() : [];
              const templateByCode = new Map(templates.map(t => [t.code, t]));
              const templateById = new Map(templates.filter(t => t.id != null).map(t => [t.id!, t]));

              const connectorSaves = get().nodes
                .filter(n => n.data.processElement?.connectorID)
                .map(n => {
                  const pe = n.data.processElement!;
                  const connector = pe.connector as any;

                  // Template lookup: prefer DataTemplateID (unique even for duplicate codes)
                  const uiCfg = pe.configuration?.designer?.ui as any;
                  const storedDtId = uiCfg?.dataTemplateID;
                  const nodeTypeCode = uiCfg?.nodeType;
                  const template = (storedDtId ? templateById.get(storedDtId) : undefined) ?? (nodeTypeCode ? templateByCode.get(nodeTypeCode) : undefined);
                  const templateCfg = (template as any)?.settings?.data?.connector?.configuration ?? {};

                  // Merge: template defaults + pe.connector.configuration (user changes win).
                  // connector?.configuration (lowercase, pre-parsed object) is preferred when present;
                  // otherwise fall back to connector?.Configuration, which is a JSON STRING (as returned
                  // by every normal connector fetch) — it must be JSON.parse()'d here, never spread
                  // directly. Spreading a string decomposes it into indexed-character properties
                  // ("0":"{", "1":"\"", ...) instead of its real keys, silently discarding the entire
                  // configuration (agentID, knowledgeBaseID, credentialID, etc.) while still producing a
                  // syntactically valid (garbage) JSON string on save — a real, previously-silent
                  // data-loss bug: the PUT below succeeds with 200 while corrupting Configuration.
                  const rawCfg = connector?.configuration ?? connector?.Configuration ?? {};
                  let existingCfg: Record<string, any> = {};
                  if (rawCfg && typeof rawCfg === 'object') {
                    existingCfg = rawCfg;
                  } else if (typeof rawCfg === 'string' && rawCfg.trim()) {
                    try {
                      existingCfg = JSON.parse(rawCfg);
                    } catch {
                      console.warn('[workflowStore] connector.Configuration is not valid JSON — skipping merge for this connector', { connectorID: pe.connectorID });
                    }
                  }
                  const mergedCfg = { ...templateCfg, ...existingCfg };
                  const hasConfig = Object.keys(mergedCfg).length > 0;
                  const hasConnectorData = !!(connector?.Name ?? connector?.name);

                  // Skip update if there is nothing to persist — the refresh endpoint does not
                  // return the nested Connector object, so pe.connector is an empty placeholder
                  // { configuration: {} }. Sending Configuration: null would OVERWRITE any
                  // existing connector configuration that was previously saved via Config dialog.
                  if (!hasConfig && !hasConnectorData) {
                    return Promise.resolve();
                  }

                  // Spread connector but drop its own "configuration"/"Configuration" keys first — the
                  // freshly-computed, correctly-stringified Configuration below is the only one that
                  // must survive. Carrying a stray lowercase "configuration" (object) forward here used
                  // to collide with the PascalCase "Configuration" (string) after wrapRequest's
                  // convertKeysToPascalCase folds them into one wire property; whichever key iterates
                  // last would win, and when it was the un-stringified object the backend's
                  // ApplyPartialUpdate threw ("could not be converted to Connector.Configuration
                  // (String)") since a JSON object can't deserialize into a string column.
                  const { configuration: _staleLowerConfig, Configuration: _staleUpperConfig, ...connectorRest } =
                    (connector && typeof connector === 'object' ? connector : {}) as Record<string, any>;
                  const updatedConnector = {
                    ...connectorRest,
                    ConnectorID: pe.connectorID,
                    Name: connector?.Name ?? connector?.name ?? pe.name ?? '',
                    Configuration: hasConfig ? JSON.stringify(mergedCfg) : null,
                  };
                  return connectorApiClient.update(updatedConnector as any);
                });

              if (connectorSaves.length > 0) {
                await Promise.allSettled(connectorSaves);
              }
            } catch (err) {
              console.warn('[workflowStore] Could not save connector configurations:', err);
            }

            return response.data;
          } else {
            // Handle validation errors or API errors
            const errorMsg =
              response.error?.message ||
              (response.data?.errors && response.data.errors.length > 0
                ? response.data.errors.map((e) => e.message).join(', ')
                : 'Save failed');
            set({ saveError: errorMsg, isSaving: false });
            return null;
          }
        } catch (error: any) {
          const errorMsg = error.message || 'Network error during save';
          set({ saveError: errorMsg, isSaving: false });
          return null;
        }
      },

      /**
       * Load workflow from backend API
       */
      loadWorkflowFromApi: async (processThreadId: number, versionId: number, workflowName?: string): Promise<void> => {
        set({ isLoading: true, loadError: null });

        try {
          const response = await workflowApiClient.refreshWorkflow(processThreadId);

          if (response.success && response.data) {
            const data = response.data;

            // Map DTOs to frontend types
            // Pass nodeTypes from store so mapper can restore visual properties
            const currentNodeTypes = get().nodeTypes;
            let nodes = mapDtosToNodes(data.nodes, undefined, currentNodeTypes);

            // The refresh endpoint returns ConnectorID and ProcessElementID in node DTOs.
            // Fetch ProcessElements directly as a safety net to ensure these IDs are always merged.
            const effectiveLoadVersionId = data.processThreadVersionID || versionId;
            try {
              const elementsResponse = await processElementApiClient.getByThreadVersion(effectiveLoadVersionId);
              console.log('[workflowStore] load-path getByThreadVersion:', {
                success: elementsResponse.success,
                count: elementsResponse.data?.length ?? 0,
                elements: elementsResponse.data?.map(el => ({
                  key: el.ProcessElementKey,
                  connectorID: el.ConnectorID,
                  processElementID: el.ProcessElementID,
                })),
              });
              if (elementsResponse.success && elementsResponse.data?.length) {
                const connectorIdByKey = new Map<string, number>();
                const processElementIdByKey = new Map<string, number>();
                for (const el of elementsResponse.data) {
                  const key = el.ProcessElementKey;
                  if (!key) continue;
                  if (el.ConnectorID) connectorIdByKey.set(key, el.ConnectorID);
                  if (el.ProcessElementID) processElementIdByKey.set(key, el.ProcessElementID);
                }
                nodes = nodes.map(node => {
                  const connectorID = connectorIdByKey.get(node.id);
                  const processElementID = processElementIdByKey.get(node.id);
                  const needsConnector = connectorID && !node.data.processElement?.connectorID;
                  const needsElementId = processElementID && !node.data.processElement?.processElementID;
                  if (connectorID !== undefined || processElementID !== undefined) {
                    console.log(`[workflowStore] load node "${node.id}": backendConnectorID=${connectorID}, currentConnectorID=${node.data.processElement?.connectorID}, needsConnector=${needsConnector}`);
                  }
                  if (needsConnector || needsElementId) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        processElement: {
                          ...node.data.processElement,
                          ...(needsConnector ? { connectorID } : {}),
                          ...(needsElementId ? { processElementID } : {}),
                        },
                      },
                    } as typeof node;
                  }
                  return node;
                });
              } else {
                console.warn('[workflowStore] load-path: getByThreadVersion returned no elements — connectorID merge skipped', {
                  success: elementsResponse.success,
                  versionId,
                });
              }
            } catch (err) {
              console.warn('[workflowStore] Could not fetch ProcessElements for merge:', err);
            }

            const edges = mapDtosToEdges(data.edges, 'smoothstep');

            // Create a minimal Workflow object from the loaded data
            const workflow: Workflow = {
              workflowId: processThreadId.toString(),
              processId: data.processID?.toString(), // Use ProcessID from response
              resId: data.processThreadResId,
              name: workflowName || data.processThreadResId || `Workflow ${processThreadId}`,  // workflowName = "{Project} Workflow 1" supplied by WorkflowApp
              versionNumber: versionId,
              versionTag: `v${versionId}`,
              isActive: true,
              isPublished: false,
              settings: {
                defaultZoom: 1,
                gridSize: 20,
                snapToGrid: false,
              },
            };

            set({
              workflow,
              nodes,
              edges,
              currentProcessThreadId: processThreadId,
              currentVersionId: effectiveLoadVersionId,
              isDirty: false,
              history: { past: [], future: [] },
              isLoading: false,
              loadError: null,
            });
          } else {
            // Brand-new workflow with no nodes yet — the refresh endpoint may return no data
            // or a "not found" response. Treat this as an empty canvas rather than an error.
            const errMsg = response.error?.message ?? '';
            const isNewWorkflow =
              !response.success &&
              (errMsg.toLowerCase().includes('not found') ||
               errMsg.toLowerCase().includes('no data') ||
               !errMsg);

            if (isNewWorkflow) {
              const workflow: Workflow = {
                workflowId: processThreadId.toString(),
                processId: processThreadId.toString(),
                name: workflowName || `Workflow ${processThreadId}`,
                versionNumber: versionId,
                versionTag: `v${versionId}`,
                isActive: true,
                isPublished: false,
                settings: { defaultZoom: 1, gridSize: 20, snapToGrid: false },
              };
              set({
                workflow,
                nodes: [],
                edges: [],
                currentProcessThreadId: processThreadId,
                currentVersionId: versionId,
                isDirty: false,
                history: { past: [], future: [] },
                isLoading: false,
                loadError: null,
              });
            } else {
              set({ loadError: errMsg || 'Load failed', isLoading: false });
            }
          }
        } catch (error: any) {
          const errorMsg = error.message || 'Network error during load';
          set({ loadError: errorMsg, isLoading: false });
        }
      },

      /**
       * Refresh workflow data (reload from server)
       */
      refreshWorkflowFromApi: async (): Promise<void> => {
        const { currentProcessThreadId, currentVersionId } = get();
        if (currentProcessThreadId && currentVersionId) {
          await get().loadWorkflowFromApi(currentProcessThreadId, currentVersionId);
        } else {
          console.warn('Cannot refresh: no workflow context set');
        }
      },

      // Load Workflow
      loadWorkflow: (workflowId: string) => {
        // Try to load from mock data, default to wf-001 (workflow1) if not found
        // Note: Mock data loading is deferred due to circular dependency with flow-studio-core
        // This function is typically called after loadWorkflowFromApi is used instead
        const data = mockWorkflowData[workflowId as keyof typeof mockWorkflowData];

        if (data) {
          set({
            workflow: data.workflow,
            nodes: data.nodes,
            edges: data.connections,
            groups: data.groups || [],
            history: { past: [], future: [] }, // Reset history on load
            isDirty: false, // Reset dirty flag on load
          });
        } else {
          console.warn(`[WorkflowStore] Mock workflow '${workflowId}' not found. Use loadWorkflowFromApi() for API-based loading.`);
        }
      },

      // Set Nodes
      setNodes: (nodes: WorkflowNode[]) => {
        set({ nodes });
      },

      // Set Edges
      setEdges: (edges: WorkflowConnection[]) => {
        set({ edges });
      },

      // Add Node
      addNode: (node: WorkflowNode) => {
        get().pushHistory();
        set((state) => ({
          nodes: [...state.nodes, node],
          isDirty: true,
        }));
        scheduleAutoSave(get);
      },

      // Update Node
      updateNode: (nodeId: string, updates: Partial<WorkflowNode>) => {
        set((state) => ({
          nodes: state.nodes.map((node) =>
            node.id === nodeId ? { ...node, ...updates } : node
          ),
          isDirty: true,
        }));
        scheduleAutoSave(get);
      },

      // Update ProcessElement fields (persisted data)
      updateProcessElement: (nodeId: string, partial: Partial<ProcessElementData>) => {
        set((state) => ({
          nodes: state.nodes.map((node) =>
            node.id === nodeId
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    processElement: { ...(node.data.processElement as any), ...partial },
                  },
                }
              : node
          ) as typeof state.nodes,
          isDirty: true,
        }));
        scheduleAutoSave(get);
      },

      // Update Runtime fields (non-persisted UI state)
      updateRuntime: (nodeId: string, partial: Partial<WorkflowNodeRuntime>) => {
        set((state) => ({
          nodes: state.nodes.map((node) =>
            node.id === nodeId
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    runtime: { ...(node.data.runtime as any), ...partial },
                  },
                }
              : node
          ) as typeof state.nodes,
        }));
      },

      // Delete Node
      deleteNode: (nodeId: string) => {
        get().pushHistory();
        set((state) => ({
          nodes: state.nodes.filter((node) => node.id !== nodeId),
          edges: state.edges.filter(
            (edge) => edge.source !== nodeId && edge.target !== nodeId
          ),
          isDirty: true,
        }));
        scheduleAutoSave(get);
      },

      runNode: async (_nodeId: string) => { /* reserved for execution engine */ },

      duplicateNode: async (_nodeId: string) => { /* reserved — use cloneNode from NodeFactoryService */ },

      // Add Edge
      addEdge: (edge: WorkflowConnection) => {
        get().pushHistory();
        set((state) => ({
          edges: [...state.edges, edge],
          isDirty: true,
        }));
        scheduleAutoSave(get);
      },

      // Update Edge
      updateEdge: (edgeId: string, updates: Partial<WorkflowConnection>) => {
        set((state) => ({
          edges: state.edges.map((edge) =>
            edge.id === edgeId ? { ...edge, ...updates } : edge
          ),
          isDirty: true,
        }));
        scheduleAutoSave(get);
      },

      // Delete Edge
      deleteEdge: (edgeId: string) => {
        get().pushHistory();
        set((state) => ({
          edges: state.edges.filter((edge) => edge.id !== edgeId),
          isDirty: true,
        }));
        scheduleAutoSave(get);
      },

      // Update all edge types
      updateAllEdgeTypes: (edgeType: string) => {
        set((state) => ({
          edges: state.edges.map((edge) => ({
            ...edge,
            type: edgeType as any,
          })),
        }));
      },

      // Add Group
      addGroup: (group: ProcessGroup) => {
        set((state) => ({
          groups: [...state.groups, group],
        }));
      },

      // Update Group
      updateGroup: (groupId: string, updates: Partial<ProcessGroup>) => {
        set((state) => ({
          groups: state.groups.map((group) =>
            group.id === groupId ? { ...group, ...updates } : group
          ),
        }));
      },

      // Delete Group
      deleteGroup: (groupId: string) => {
        set((state) => ({
          groups: state.groups.filter((group) => group.id !== groupId),
        }));
      },

      // Undo
      undo: () => {
        const { history, nodes, edges } = get();
        if (history.past.length === 0) return;

        const previous = history.past[history.past.length - 1];
        const newPast = history.past.slice(0, history.past.length - 1);

        set({
          nodes: previous.nodes,
          edges: previous.edges,
          history: {
            past: newPast,
            future: [{ nodes, edges }, ...history.future],
          },
          // If we still have changes in history, we're dirty
          isDirty: newPast.length > 0,
        });
      },

      // Redo
      redo: () => {
        const { history, nodes, edges } = get();
        if (history.future.length === 0) return;

        const next = history.future[0];
        const newFuture = history.future.slice(1);

        set({
          nodes: next.nodes,
          edges: next.edges,
          history: {
            past: [...history.past, { nodes, edges }],
            future: newFuture,
          },
          // After redo, we have changes so we're dirty
          isDirty: true,
        });
      },

      // Can Undo
      canUndo: () => {
        return get().history.past.length > 0;
      },

      // Can Redo
      canRedo: () => {
        return get().history.future.length > 0;
      },

      // Push History
      pushHistory: () => {
        const { nodes, edges, history } = get();
        set({
          history: {
            past: [...history.past, { nodes: [...nodes], edges: [...edges] }],
            future: [], // Clear future on new action
          },
        });
      },

      // Reset
      reset: () => {
        set({
          workflow: null,
          nodes: [],
          edges: [],
          groups: [],
          history: { past: [], future: [] },
        });
      },
    }),
    { name: 'WorkflowStore' }
  )
);

/**
 * Initialize Node Templates
 * Call this function during app startup to load node types and categories
 */
export { NODE_TEMPLATE_CLIENT_OPTIONS } from './nodeTemplateClientOptions';
import { NODE_TEMPLATE_CLIENT_OPTIONS } from './nodeTemplateClientOptions';

const nodeTemplateService = new NodeTemplateService(NODE_TEMPLATE_CLIENT_OPTIONS);

export async function initializeNodeTemplates(): Promise<void> {
  try {
    await nodeTemplateService.initialize();
    // Use getTemplates() (full NodeTemplate with settings) so drag-drop can seed
    // processElement.configuration and connector.configuration from template defaults.
    const nodeTypes = nodeTemplateService.getTemplates() as any[];
    const nodeCategories = nodeTemplateService.getCategories();

    // Update the workflow store with loaded data
    useWorkflowStore.getState().setNodeTypes(nodeTypes);
    useWorkflowStore.getState().setNodeCategories(nodeCategories);
  } catch (error) {
    console.error('Failed to initialize node templates:', error);
    // Continue with empty defaults instead of crashing
    console.warn('Node templates initialization failed, continuing with empty defaults');
    useWorkflowStore.getState().setNodeTypes([]);
    useWorkflowStore.getState().setNodeCategories([]);
  }
}

/**
 * Refresh Node Templates
 * Call this function to reload templates from the server
 * Useful after template files are updated or during development
 */
export async function refreshNodeTemplates(): Promise<void> {
  try {
    await nodeTemplateService.refresh();
    const nodeTypes = nodeTemplateService.getTemplates() as any[];
    const nodeCategories = nodeTemplateService.getCategories();

    // Update the workflow store with refreshed data
    useWorkflowStore.getState().setNodeTypes(nodeTypes);
    useWorkflowStore.getState().setNodeCategories(nodeCategories);
  } catch (error) {
    console.error('Failed to refresh node templates:', error);
    console.warn('Node templates refresh failed, keeping existing data');
    // Don't throw - keep existing data
  }
}
